import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import {
  FaUsers,
  FaArrowUp,
  FaArrowDown,
  FaSearch,
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import "./GroupLedgerSummary.css";
import { TableRowsSkeleton } from "../../Component/Skeleton";
import SearchableSelect from "../../Component/SearchableSelect/SearchableSelect";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable already applied or fallback
}

const formatINR = (val) => {
  const num = Number(val || 0);
  if (!num || num === 0) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function GroupLedgerSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCompany } = useCompany();
  const BASE_API_URL =
    process.env.REACT_APP_BASE_API_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000/api";

  const companyId = useMemo(() => {
    return (
      selectedCompany?.CompID ||
      selectedCompany?.id ||
      sessionStorage.getItem("CompID") ||
      sessionStorage.getItem("companyId") ||
      "1147"
    );
  }, [selectedCompany]);

  const companyInfo = useMemo(() => {
    return {
      name: selectedCompany?.Name || selectedCompany?.name || selectedCompany?.CompanyName || "DAZY TOURE TRAVEL'S",
      address: selectedCompany?.Address || selectedCompany?.address || "Opp. GPO, M.I. Road, Jaipur-302001",
      gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "",
      stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
    };
  }, [selectedCompany]);

  const todayStr = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);

  // Parse URL search params or saved session state to preserve state when navigating back
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlGroupCode = searchParams.get("groupCode");
  const urlGroupName = searchParams.get("groupName");
  const urlAsOnDate = searchParams.get("asOnDate");
  const urlFilter = searchParams.get("filter");

  const savedState = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("gls_saved_state");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const [groups, setGroups] = useState([]);
  const [selectedGroupCode, setSelectedGroupCode] = useState(() => {
    if (urlGroupCode) return urlGroupCode;
    if (savedState?.selectedGroupCode) return savedState.selectedGroupCode;
    return "all";
  });
  const [asOnDate, setAsOnDate] = useState(() => {
    if (urlAsOnDate) return urlAsOnDate;
    if (savedState?.asOnDate) return savedState.asOnDate;
    return todayStr;
  });
  const [balanceFilter, setBalanceFilter] = useState(() => {
    if (urlFilter) return urlFilter;
    if (savedState?.balanceFilter) return savedState.balanceFilter;
    return "non-zero";
  });
  const [groupInfo, setGroupInfo] = useState(() => ({
    groupCode: urlGroupCode || "all",
    groupName: urlGroupName ? decodeURIComponent(urlGroupName) : "All Groups",
  }));
  const [ledgers, setLedgers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(() => savedState?.searchTerm || "");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => savedState?.currentPage || 1);
  const [rowsPerPage, setRowsPerPage] = useState(() => savedState?.rowsPerPage || 25);

  // When URL search params change (e.g. user clicked a group in Profit & Loss)
  useEffect(() => {
    if (urlGroupCode) {
      setSelectedGroupCode(urlGroupCode);
    }
    if (urlGroupName) {
      setGroupInfo((prev) => ({
        ...prev,
        groupCode: urlGroupCode || prev.groupCode,
        groupName: decodeURIComponent(urlGroupName),
      }));
    }
    if (urlAsOnDate) {
      setAsOnDate(urlAsOnDate);
    }
    if (urlFilter) {
      setBalanceFilter(urlFilter);
    }
  }, [urlGroupCode, urlGroupName, urlAsOnDate, urlFilter]);

  // Sync state into sessionStorage to preserve state on back navigation
  useEffect(() => {
    sessionStorage.setItem(
      "gls_saved_state",
      JSON.stringify({
        selectedGroupCode,
        asOnDate,
        balanceFilter,
        searchTerm,
        currentPage,
        rowsPerPage,
      })
    );
  }, [selectedGroupCode, asOnDate, balanceFilter, searchTerm, currentPage, rowsPerPage]);

  // Live timestamp for "As on" badge
  const asOnTime = useMemo(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const mNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const mon = mNames[now.getMonth()];
    const year = now.getFullYear();
    const hours = now.getHours();
    const mins = String(now.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const h12 = hours % 12 || 12;
    return `${day}-${mon}-${year} ${h12}:${mins} ${ampm}`;
  }, []);

  // Fetch groups list for dropdown
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${BASE_API_URL}/groups?page=1&limit=1000`);
        const rawList = res.data?.allGroups || res.data?.data || (Array.isArray(res.data) ? res.data : []);
        const mappedGroups = rawList.map((g) => ({
          GroupCODE: String(g.GroupCODE),
          GroupName: g.GroupName || g.GroupNAME || `Group ${g.GroupCODE}`,
        }));
        setGroups(mappedGroups);
      } catch (err) {
        console.error("Failed to fetch groups:", err);
      }
    };
    fetchGroups();
  }, [BASE_API_URL]);

  // Fetch Group Ledger Summary from API
  const fetchGroupSummary = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = { asOnDate };
      if (selectedGroupCode && selectedGroupCode !== "all") {
        params.groupCode = selectedGroupCode;
      }

      const res = await axios.get(`${BASE_API_URL}/reports/ledger/group-summary/${companyId}`, { params });
      if (res.data?.success) {
        setLedgers(res.data.data || []);
        if (res.data.group) {
          setGroupInfo(res.data.group);
        }
      } else {
        setLedgers([]);
        showToast.info("No ledgers found for selected criteria");
      }
    } catch (err) {
      console.error("Failed to load group ledger summary:", err);
      setLedgers([]);
      showToast.error("Failed to load group ledger summary");
    } finally {
      setLoading(false);
    }
  }, [BASE_API_URL, companyId, selectedGroupCode, asOnDate]);

  useEffect(() => {
    if (companyId) {
      fetchGroupSummary();
    }
  }, [companyId, selectedGroupCode, fetchGroupSummary]);

  const handleReset = () => {
    sessionStorage.removeItem("gls_saved_state");
    setSelectedGroupCode("all");
    setBalanceFilter("non-zero");
    setAsOnDate(todayStr);
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Filter ledgers by balance and search term
  const filteredLedgers = useMemo(() => {
    return ledgers.filter((l) => {
      // Balance filter: 'non-zero' excludes ledgers where debit and credit are both 0 (and closingBalance is 0)
      if (balanceFilter === "non-zero") {
        const debit = Number(l.totalDebit || 0);
        const credit = Number(l.totalCredit || 0);
        const closing = Number(l.closingBalance || 0);
        if (Math.abs(debit) <= 0.0001 && Math.abs(credit) <= 0.0001 && Math.abs(closing) <= 0.0001) {
          return false;
        }
      }
      // Search term filter
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        if (!l.ledgerName || !l.ledgerName.toLowerCase().includes(term)) {
          return false;
        }
      }
      return true;
    });
  }, [ledgers, searchTerm, balanceFilter]);

  // Paginated records
  const totalPages = Math.ceil(filteredLedgers.length / rowsPerPage) || 1;
  const paginatedLedgers = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredLedgers.slice(start, start + rowsPerPage);
  }, [filteredLedgers, currentPage, rowsPerPage]);

  // Calculate totals for filtered list (Sum of Debit and Sum of Credit)
  const filteredTotals = useMemo(() => {
    let totDebit = 0;
    let totCredit = 0;
    filteredLedgers.forEach((l) => {
      totDebit += Number(l.totalDebit || 0);
      totCredit += Number(l.totalCredit || 0);
    });

    return {
      totalDebit: Math.round(totDebit * 100) / 100,
      totalCredit: Math.round(totCredit * 100) / 100,
    };
  }, [filteredLedgers]);

  // Current active group display name
  const activeGroupName = useMemo(() => {
    if (urlGroupName) return decodeURIComponent(urlGroupName);
    if (!selectedGroupCode || selectedGroupCode === "all") return "All Groups";
    const found = groups.find((g) => String(g.GroupCODE) === String(selectedGroupCode));
    return found ? found.GroupName : groupInfo.groupName || "Selected Group";
  }, [selectedGroupCode, groups, groupInfo, urlGroupName]);

  const safeGroupName = useMemo(() => {
    return activeGroupName
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/__+/g, "_")
      .replace(/^_|_$/g, "");
  }, [activeGroupName]);

  // Navigate to Monthly Summary on ledger click
  const handleLedgerClick = (headCode) => {
    navigate("/monthly-ledger-summary", {
      state: { accountId: String(headCode) },
    });
  };

  // Export to Excel
  const handleExportExcel = () => {
    try {
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
      const reportTitle = "GROUP LEDGER SUMMARY";
      const filterLabel = balanceFilter === "non-zero" ? "Non Zero" : "All";
      const periodSubtitle = `Group: ${activeGroupName}  |  As on Date: ${asOnDate}  |  Filter: ${filterLabel}  |  Generated: ${asOnTime}`;

      const headers = ["S.No.", "Ledger Name", "Debit (INR)", "Credit (INR)"];

      const dataRows = filteredLedgers.map((r, idx) => [
        idx + 1,
        r.ledgerName,
        r.totalDebit > 0 ? r.totalDebit : "-",
        r.totalCredit > 0 ? r.totalCredit : "-",
      ]);

      const totalRow = [
        "Total",
        "",
        filteredTotals.totalDebit,
        filteredTotals.totalCredit,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        ...dataRows,
        totalRow,
        [],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 3 } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: 3 } },
      ];

      worksheet["!cols"] = [
        { wch: 8 },  // S.No
        { wch: 45 }, // Ledger Name
        { wch: 20 }, // Debit
        { wch: 20 }, // Credit
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Group_Ledgers");
      const fileName = `Group_Ledger_Summary_${safeGroupName}_${asOnDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export Excel");
    }
  };

  // Export to PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [
        companyInfo.address,
        companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "",
        companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "",
      ]
        .filter(Boolean)
        .join("   |   ");
      const reportTitle = "GROUP LEDGER SUMMARY";

      // 1. Company Name Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      // 2. Company Details
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      // 3. Report Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.setLineWidth(0.75);
      doc.setDrawColor(0, 0, 0);
      doc.line(pageWidth / 2 - titleWidth / 2, 27.5, pageWidth / 2 + titleWidth / 2, 27.5);

      // 4. Divider Line
      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      // 5. Meta Information Bar
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      const filterLabel = balanceFilter === "non-zero" ? "Non Zero" : "All";
      doc.text(`Group: ${activeGroupName}   |   Filter: ${filterLabel}`, 14, 36);
      doc.text(`As On: ${asOnDate}   |   Date: ${asOnTime}`, pageWidth - 14, 36, { align: "right" });

      // 6. Divider Line below Meta bar
      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      const tableRows = [
        ...filteredLedgers.map((l, idx) => [
          idx + 1,
          l.ledgerName,
          l.totalDebit > 0 ? formatINR(l.totalDebit) : "-",
          l.totalCredit > 0 ? formatINR(l.totalCredit) : "-",
        ]),
        [
          "",
          "Total",
          formatINR(filteredTotals.totalDebit),
          formatINR(filteredTotals.totalCredit),
        ],
      ];

      autoTable(doc, {
        head: [
          [
            { content: "S.No.", styles: { halign: "center" } },
            { content: "Ledger Name", styles: { halign: "left" } },
            { content: "Debit (INR)", styles: { halign: "right" } },
            { content: "Credit (INR)", styles: { halign: "right" } },
          ],
        ],
        body: tableRows,
        startY: 42,
        theme: "plain",
        styles: {
          fontSize: 8.5,
          cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
          textColor: [0, 0, 0],
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fontStyle: "bold",
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: { top: 0.5, bottom: 0.5 },
        },
        columnStyles: {
          0: { cellWidth: 15, halign: "center" },
          1: { cellWidth: "auto", halign: "left" },
          2: { cellWidth: 35, halign: "right" },
          3: { cellWidth: 35, halign: "right" },
        },
        didParseCell: (data) => {
          if (data.row.index === tableRows.length - 1) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.lineWidth = { top: 0.5, bottom: 0.5 };
            data.cell.styles.lineColor = [0, 0, 0];
          }
        },
        margin: { left: 14, right: 14, bottom: 18 },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageCurrent = data.pageNumber;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(100, 100, 100);
          doc.text("etour - Design & Develop by Response Infoway", 14, 290);
          doc.text(`Page ${pageCurrent} of ${pageCount}`, pageWidth - 14, 290, { align: "right" });
        },
      });

      const fileName = `Group_Ledger_Summary_${safeGroupName}_${asOnDate}.pdf`;
      doc.save(fileName);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error(err);
      showToast.error("Failed to export PDF");
    }
  };

  // Dedicated Print handler
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="gls_page_wrapper">
      {/* Header & Breadcrumb Bar */}
      <div className="gls_header_section">
        <div className="gls_breadcrumb_bar">
          <Link to="/" style={{ color: "#64748b", textDecoration: "none" }}>
            Reports
          </Link>
          <span>&gt;</span>
          <span style={{ color: "#1e3a8a", fontWeight: 700 }}>Group Ledger Summary</span>
        </div>

        <div className="gls_as_on_badge">
          <FaClock />
          <span>As on: {asOnTime}</span>
        </div>
      </div>

      {/* Filter Card */}
      <div className="gls_filter_card">
        <div className="gls_filter_bar">
          {/* Account Group Dropdown */}
          <div className="gls_filter_group">
            <label className="gls_filter_label">
              Account Group<span className="required_star">*</span>:
            </label>
            <SearchableSelect
              options={[
                { value: "all", label: "All Groups" },
                ...groups.map((g) => ({ value: g.GroupCODE, label: g.GroupName })),
              ]}
              value={selectedGroupCode}
              onChange={(val) => {
                setSelectedGroupCode(val || "all");
                setCurrentPage(1);
              }}
              placeholder="-- Type to search group --"
              height="28px"
              minWidth="220px"
              isClearable={false}
            />
          </div>

          {/* As On Date Picker */}
          <div className="gls_filter_group">
            <label className="gls_filter_label">
              As On Date<span className="required_star">*</span>:
            </label>
            <input
              type="date"
              className="filter-input"
              value={asOnDate}
              onChange={(e) => {
                setAsOnDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          {/* Filter: All / Non Zero Toggle */}
          <div className="gls_filter_group">
            <span className="gls_filter_label">Filter:</span>
            <div className="gls_toggle_group">
              <button
                type="button"
                className={`gls_toggle_btn ${balanceFilter === "all" ? "active" : ""}`}
                onClick={() => {
                  setBalanceFilter("all");
                  setCurrentPage(1);
                }}
              >
                All
              </button>
              <button
                type="button"
                className={`gls_toggle_btn ${balanceFilter === "non-zero" ? "active" : ""}`}
                onClick={() => {
                  setBalanceFilter("non-zero");
                  setCurrentPage(1);
                }}
              >
                Non Zero
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="gls_filter_buttons">
            <button type="button" className="gls_btn_apply" onClick={fetchGroupSummary}>
              View
            </button>
            <button type="button" className="gls_btn_reset" onClick={handleReset}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards Row (3 Cards matching Image 1) */}
      <div className="gls_kpi_row">
        {/* Card 1: Total Ledgers */}
        <div className="gls_kpi_card">
          <div className="gls_kpi_icon_box gls_icon_ledgers">
            <FaUsers />
          </div>
          <div className="gls_kpi_content">
            <span className="gls_kpi_label">TOTAL LEDGERS IN GROUP</span>
            <span className="gls_kpi_value">{filteredLedgers.length}</span>
          </div>
        </div>

        {/* Card 2: Total Debit Balance */}
        <div className="gls_kpi_card">
          <div className="gls_kpi_icon_box gls_icon_debit">
            <FaArrowUp />
          </div>
          <div className="gls_kpi_content">
            <span className="gls_kpi_label">TOTAL DEBIT BALANCE</span>
            <span className="gls_kpi_value">
              ₹ {formatINR(filteredTotals.totalDebit)}{" "}
              <span className="drcr_text_dr">Dr</span>
            </span>
          </div>
        </div>

        {/* Card 3: Total Credit Balance */}
        <div className="gls_kpi_card">
          <div className="gls_kpi_icon_box gls_icon_credit">
            <FaArrowDown />
          </div>
          <div className="gls_kpi_content">
            <span className="gls_kpi_label">TOTAL CREDIT BALANCE</span>
            <span className="gls_kpi_value">
              ₹ {formatINR(filteredTotals.totalCredit)}{" "}
              <span className="drcr_text_cr">Cr</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="gls_table_card">
        {/* Table Toolbar */}
        <div className="gls_table_toolbar">
          <h3 className="gls_table_title">
            Ledgers in Group ({activeGroupName})
          </h3>

          <div className="gls_toolbar_right">
            {/* Search Box */}
            <div className="gls_search_box">
              <FaSearch className="gls_search_icon" />
              <input
                type="text"
                className="gls_search_input"
                placeholder="Search ledger name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Action Buttons */}
            <button
              type="button"
              className="gls_export_btn btn_excel"
              onClick={handleExportExcel}
              title="Export to Microsoft Excel"
            >
              <FaFileExcel />
              Export Excel
            </button>
            <button
              type="button"
              className="gls_export_btn btn_pdf"
              onClick={handleExportPDF}
              title="Export to PDF"
            >
              <FaFilePdf />
              Export PDF
            </button>
            <button
              type="button"
              className="gls_export_btn btn_print"
              onClick={handlePrint}
              title="Print Summary"
            >
              <FaPrint />
              Print
            </button>
          </div>
        </div>

        {/* Table Wrapper */}
        <div className="gls_table_wrapper">
          <table className="gls_custom_table">
            <thead>
              <tr>
                <th style={{ width: "60px", textAlign: "center" }}>S.No.</th>
                <th>Ledger Name</th>
                <th style={{ textAlign: "right", width: "160px" }}>Debit (₹)</th>
                <th style={{ textAlign: "right", width: "160px" }}>Credit (₹)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <TableRowsSkeleton columns={4} rows={5} />
              ) : filteredLedgers.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: "center", padding: "20px", color: "#a0aec0" }}>
                    No ledger records found for this group.
                  </td>
                </tr>
              ) : (
                paginatedLedgers.map((row, idx) => (
                  <tr
                    key={row.headCode || idx}
                    onClick={() => handleLedgerClick(row.headCode)}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ textAlign: "center", color: "#718096" }}>
                      {(currentPage - 1) * rowsPerPage + idx + 1}
                    </td>
                    <td>
                      <span
                        className="gls_ledger_link"
                        title="View monthly statement for this ledger"
                      >
                        {row.ledgerName}
                      </span>
                    </td>
                    <td className="gls_table_num">
                      {row.totalDebit > 0 ? formatINR(row.totalDebit) : ""}
                    </td>
                    <td className="gls_table_num">
                      {row.totalCredit > 0 ? formatINR(row.totalCredit) : ""}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {filteredLedgers.length > 0 && (
              <tfoot>
                <tr className="gls_table_total_row">
                  <td style={{ textAlign: "center" }}></td>
                  <td>Total</td>
                  <td className="gls_table_num">
                    {formatINR(filteredTotals.totalDebit)}
                  </td>
                  <td className="gls_table_num">
                    {formatINR(filteredTotals.totalCredit)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Pagination Row */}
        {filteredLedgers.length > 0 && (
          <div className="gls_pagination_row">
            <div>
              Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
              {Math.min(currentPage * rowsPerPage, filteredLedgers.length)} of{" "}
              {filteredLedgers.length} records
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div className="gls_pagination_btns">
                <button
                  type="button"
                  className="gls_page_nav_btn"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  title="Previous Page"
                >
                  <FaChevronLeft size={10} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => {
                  if (
                    pg === 1 ||
                    pg === totalPages ||
                    (pg >= currentPage - 2 && pg <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={pg}
                        type="button"
                        className={`gls_page_num_btn ${currentPage === pg ? "active" : ""}`}
                        onClick={() => setCurrentPage(pg)}
                      >
                        {pg}
                      </button>
                    );
                  }
                  if (pg === currentPage - 3 || pg === currentPage + 3) {
                    return (
                      <span key={pg} style={{ padding: "0 4px", color: "#a0aec0" }}>
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
                <button
                  type="button"
                  className="gls_page_nav_btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  title="Next Page"
                >
                  <FaChevronRight size={10} />
                </button>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span>Rows per page:</span>
                <select
                  className="gls_rows_select"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* DEDICATED PRINT CONTAINER (Hidden on screen, Active during @media print) */}
      <div className="gls_print_area d-none">
        {/* Company Header */}
        <div style={{ textAlign: "center", borderBottom: "2px solid #000000", paddingBottom: "8px", marginBottom: "10px" }}>
          <h2 style={{ margin: "0 0 3px 0", fontSize: "19px", fontWeight: "bold", textTransform: "uppercase", color: "#000000" }}>
            {companyInfo.name}
          </h2>
          <div style={{ fontSize: "11px", color: "#222222", lineHeight: 1.4 }}>
            {companyInfo.address && `${companyInfo.address}  |  `}
            <strong>GSTIN:</strong> {companyInfo.gstin}
            {companyInfo.stateCode && `  |  State Code: ${companyInfo.stateCode}`}
          </div>
          <div style={{ marginTop: "6px", fontSize: "13px", fontWeight: "bold", color: "#000000" }}>
            GROUP LEDGER SUMMARY
          </div>
        </div>

        {/* Print Filter Meta */}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "8px", borderBottom: "1px solid #000000", paddingBottom: "4px" }}>
          <div>
            <strong>Group:</strong> {activeGroupName}  |  <strong>As on Date:</strong> {asOnDate}
          </div>
          <div style={{ textAlign: "right" }}>
            <strong>Date:</strong> {asOnTime}  |  <strong>Records:</strong> {filteredLedgers.length}
          </div>
        </div>

        {/* Table */}
        <table className="gls_print_table">
          <thead>
            <tr>
              <th style={{ width: "8%", textAlign: "center" }}>S.No.</th>
              <th style={{ width: "52%" }}>Ledger Name</th>
              <th style={{ width: "20%", textAlign: "right" }}>Debit (₹)</th>
              <th style={{ width: "20%", textAlign: "right" }}>Credit (₹)</th>
            </tr>
          </thead>
          <tbody>
            {filteredLedgers.map((row, idx) => (
              <tr key={row.headCode || idx} className={idx % 2 === 1 ? "print_row_alt" : ""}>
                <td style={{ textAlign: "center" }}>{idx + 1}</td>
                <td style={{ fontWeight: 500 }}>{row.ledgerName}</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {row.totalDebit > 0 ? formatINR(row.totalDebit) : "-"}
                </td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {row.totalCredit > 0 ? formatINR(row.totalCredit) : "-"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="print_total_row">
              <td style={{ textAlign: "center" }}></td>
              <td style={{ fontWeight: "bold" }}>Total</td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                {formatINR(filteredTotals.totalDebit)}
              </td>
              <td style={{ textAlign: "right", fontWeight: "bold" }}>
                {formatINR(filteredTotals.totalCredit)}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Footer */}
        <div className="gls_print_footer">
          <span>etour - Design & Develop by Response Infoway</span>
          <span>Printed on: {asOnTime}</span>
        </div>
      </div>
    </div>
  );
}
